import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "../i18n";
import { useAuth } from "../auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Cat, Loader2 } from "lucide-react";
import PasswordInput from "../components/PasswordInput";
import GoogleSignInButton, {
  isGoogleSignInAvailable,
} from "../components/GoogleSignInButton";

export default function Register() {
  const { t, lang } = useI18n();
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "public", volunteer_type: "individual" });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (form.role !== "volunteer") delete payload.volunteer_type;
      await register(payload);
      toast.success("Account created!");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-6 py-12 bg-stone-50">
      <div className="w-full max-w-md bg-white border border-stone-200 rounded-3xl p-8 shadow-sm" data-testid="register-card">
        <Link to="/" className="flex items-center gap-2 mb-6">
          <span className="w-10 h-10 rounded-xl bg-orange-600 grid place-items-center text-white">
            <Cat size={20} />
          </span>
          <div className="font-display font-bold">{t("appName")}</div>
        </Link>
        <h1 className="font-display font-bold text-3xl tracking-tight">{t("auth.registerTitle")}</h1>

        {/* Same endpoint signs up and signs in, so there is nothing to choose
            here — one tap and the account exists.

            Absent on iOS (App Store Guideline 4.8), divider included. */}
        {isGoogleSignInAvailable() && (
          <>
            <div className="mt-6">
              <GoogleSignInButton
                isTr={lang === "tr"}
                onSuccess={() => {
                  toast.success(lang === "tr" ? "Hoş geldin!" : "Welcome!");
                  nav("/dashboard");
                }}
              />
            </div>

            <div className="flex items-center gap-3 my-5">
              <span className="h-px flex-1 bg-stone-200" />
              <span className="text-xs text-stone-400 font-medium">
                {lang === "tr" ? "veya" : "or"}
              </span>
              <span className="h-px flex-1 bg-stone-200" />
            </div>
          </>
        )}

        <form onSubmit={submit} className="space-y-4 mt-6">
          <div className="space-y-1.5">
            <Label htmlFor="name">{t("auth.name")}</Label>
            <Input id="name" required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="register-name-input" className="rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">{t("auth.email")}</Label>
            <Input id="email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="register-email-input" className="rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">{t("auth.password")}</Label>
            <PasswordInput id="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="register-password-input" className="rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("auth.role")}</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger data-testid="register-role-select" className="rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">{t("auth.rolePublic")}</SelectItem>
                <SelectItem value="volunteer">{t("auth.roleVolunteer")}</SelectItem>
                <SelectItem value="admin">{t("auth.roleAdmin")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.role === "volunteer" && (
            <div className="space-y-1.5" data-testid="volunteer-type-field">
              <Label>{t("auth.volunteerType")}</Label>
              <Select value={form.volunteer_type} onValueChange={(v) => set("volunteer_type", v)}>
                <SelectTrigger data-testid="register-volunteer-type-select" className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">{t("auth.volunteerTypeIndividual")}</SelectItem>
                  <SelectItem value="vet">{t("auth.volunteerTypeVet")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <Button type="submit" disabled={loading} className="w-full rounded-xl bg-orange-600 hover:bg-orange-700 gap-2" data-testid="register-submit-btn">
            {loading && <Loader2 size={16} className="animate-spin" />}
            {t("auth.submitRegister")}
          </Button>
        </form>
        <div className="mt-4 text-sm text-stone-500 text-center">
          <Link to="/login" className="hover:text-orange-700" data-testid="switch-login-link">
            {t("auth.switchToLogin")}
          </Link>
        </div>
      </div>
    </div>
  );
}
